export class Utils {

    static setViewHeightProperty() {
        let vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);

        window.addEventListener('resize', this.debounce(() => {
            // We execute the same script as before
            let vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        }, 1 / 30 * 1000));
    }

    static generateRandomId() {
        return Math.random().toString(36).substring(2, 15);
    }

    static trapFocus(e, firstFocusableEl, lastFocusableEl) {
        let isTabPressed = (e.key === 'Tab' || e.keyCode === 9);

        if (!isTabPressed) {
            return;
        }
        if (e.shiftKey) {
            if (document.activeElement === firstFocusableEl) {
                lastFocusableEl.focus();
                e.preventDefault();
            }
        } else {
            if (document.activeElement === lastFocusableEl) {
                firstFocusableEl.focus();
                e.preventDefault();
            }
        }

    }

    static getTrapFocusHandler(firstFocusableEl, lastFocusableEl) {
        return (e) => {
            Utils.trapFocus(e, firstFocusableEl, lastFocusableEl);
        }
    }


    static throttle(func, wait) {
        let waiting = false;
        return function () {
            if (waiting) {
                return;
            }

            waiting = true;
            setTimeout(() => {
                func.apply(this, arguments);
                waiting = false;
            }, wait);
        };
    }

    static isElementInView(element) {
        let bounding = element.getBoundingClientRect();

        return (
            bounding.top >= 0 &&
            bounding.left >= 0 &&
            bounding.bottom <=
            (window.innerHeight || document.documentElement.clientHeight) &&
            bounding.right <=
            (window.innerWidth || document.documentElement.clientWidth)
        );
    };

    // On elements that have a data-href attribute, we want to navigate to that URL
    // when the element is clicked.
    static handleNavigableEls(el, targetParent = false) {
      const childBlankAnchor = el.querySelector('a[target="_blank"]');
      const shouldOpenInNewTab = childBlankAnchor ? true : false;

        el.addEventListener('mouseover', (event) => {
            el.style.cursor = 'pointer';
        });
        el.addEventListener('mouseout', (event) => {
            el.style.cursor = '';
        });
        el.addEventListener('click', (event) => {
            // if the clicked element within el has its own href, return
            if (event.target.hasAttribute('href')) {
                return;
            }
            event.preventDefault();
            let url = el.getAttribute('data-href');
            console.log(url, shouldOpenInNewTab, targetParent);
            if (url) {
              if (shouldOpenInNewTab) {
                window.open(url, '_blank');
              } else if(targetParent) {
                window.parent.location.href = url;
              } else {
                window.location.href = url;
              }
            }
        });

        // If the element is in focus and "enter" is pressed, navigate
        el.addEventListener('keypress', (event) => {
            if (event.key === 'Enter') {
                let url = el.getAttribute('data-href');
                if (url) {
                  const isParent = el.getAttribute('data-target-parent');
                  if (isParent) {
                    if(shouldOpenInNewTab) {
                      window.open(url, '_blank');
                    } else {
                      window.parent.location.href = url;
                    }
                  } else {
                    if(shouldOpenInNewTab) {
                      window.open(url, '_blank');
                    } else {
                      window.location.href = url;
                    }
                  }
                }
            }
        });
    }

  // Hide an reveal elements using height attribute. Set the transition specific to your element as needed.
  static toggleHeight(element) {
    const style = window.getComputedStyle(element);
    const height = parseInt(style.height, 10);
    if (height > 0) {
      element.style.height = element.scrollHeight + 'px';
      element.offsetHeight; // This line doesn’t do anything other than trigger a reflow
      window.requestAnimationFrame(function() {
        element.style.height = '0px';
      });
    } else {
      element.style.height = 'auto';
      const fullHeight = element.scrollHeight + 'px';
      element.style.height = '0px';
      window.requestAnimationFrame(function() {
        element.style.height = fullHeight;
      });
    }
  }

    // Debounce function. Waits n milliseconds to fire the callback function. If multiple
    // triggers occur back to back it waits until the time has been reached to call the function.
    static debounce(func, wait, immediate) {
        let timeout;
        return function () {
            let context = this, args = arguments;
            let later = function () {
                timeout = null;
                if (!immediate) func.apply(context, args);
            };
            let callNow = immediate && !timeout;
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
            if (callNow) func.apply(context, args);
        };
    };

    static toggleFullScreenProgress(show) {
      const fullScreenProgressRoot = document.querySelector('.fullscreen-progress-overlay');
      if(!fullScreenProgressRoot) {
        return;
      }

      if (show) {
        fullScreenProgressRoot.showModal();
        window.fullScreenProgress.animateInOnLoop();
      } else {
        window.fullScreenProgress.stopAnimate();
        fullScreenProgressRoot.close();
      }
    }

    // Returns the largest size image url from a srcset of image urls and widths
    static getLargestSrcFromSrcset(srcset) {
      // We use a space here because the srcs have a bunch of commas in them and space occurs just before the width property.
      const sourcesAndWidths = srcset.split(' ');
      const sources = sourcesAndWidths.filter((src, index) => index % 2 === 0);

      const sourcesWithWidths = sources.map((src, index) => {
        return {
          url: src,
          width: parseInt(sourcesAndWidths[index * 2 + 1], 10)
        };
      });
      // Biggest width first
      sourcesWithWidths.sort((a, b) => b.width - a.width);

      // Get the URL of the largest/first image
      return sourcesWithWidths.length > 0 ? sourcesWithWidths[0].url : '';
    }

    // Adjust the width of the datatable to fit the screen
    // Note: This is a workaround for an issue in DataTables where the table width is not calculated correctly. The only way I could get width to work is to provide an explicit fixed value in DT's scrollX option. Since the option requires a fixed value, which does not work for all screen sizes, this mimics the behavior of the scrollX option by setting an inline width style on .dt-scroll-head, .dt-scroll-body, and .dt-scroll-foot elements.
    static adjustTableWidth(tableEl) {
        const isDesktop = window.matchMedia('(min-width: 768px)').matches;
        const dashboardCardPadding = 2 * parseFloat(window.getComputedStyle(document.querySelector('.dashboard-card')).getPropertyValue('padding-left'));
        const mainEl = document.querySelector('main');
        const mainElPadding = 2 * parseFloat(window.getComputedStyle(mainEl).getPropertyValue('padding-left'));
        let mainElGap = 0;
        let totalPadding = (dashboardCardPadding + mainElPadding);
        let sidebarWidth = 0;
        if (isDesktop) {
            mainElGap = parseFloat(window.getComputedStyle(mainEl).getPropertyValue('column-gap'));
            sidebarWidth = parseFloat(window.getComputedStyle(document.querySelector('.dashboard-sidebar')).getPropertyValue('width'));
        }
        totalPadding += sidebarWidth + mainElGap

        let tableParts = tableEl.querySelectorAll('.dt-scroll-head, .dt-scroll-body, .dt-scroll-foot');
        tableParts.forEach((part) => {
            part.style.width = `calc(100vw - ${totalPadding}px`;
        });
    }

}
